import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../client";
import { transformPricingResponse } from "./pricing-transformers";
import { createPricingInitialState, AUDIT_ACTIONS } from "./pricing-constants";
import {
  createAsyncHandler,
  addAuditEntry,
  updateConfigByLocality,
  extractResponseData,
  ensureArray,
} from "./pricing-reducer-helpers";

const PRICING_URL = "shared/pricingconfig";

// ============ LEGACY THUNKS (Locality-based) ============
export const getPricingConfigs = createAsyncThunk(
  "Pricing/getAll",
  async () => (await api.get(PRICING_URL)).data,
);

export const getPricingConfigByLocality = createAsyncThunk(
  "Pricing/getByLocality",
  async (localityType) => (await api.get(`${PRICING_URL}/${localityType}`)).data,
);

export const savePricingConfig = createAsyncThunk(
  "Pricing/save",
  async (data) => (await api.post(PRICING_URL, data)).data,
);

export const updatePricingConfig = createAsyncThunk(
  "Pricing/update",
  async (data) => {
    const { localityType, ...updateData } = data;
    return (await api.put(`${PRICING_URL}/${localityType}`, updateData)).data;
  },
);

export const deletePricingConfig = createAsyncThunk(
  "Pricing/delete",
  async (localityType) => (await api.delete(`${PRICING_URL}/${localityType}`)).data,
);

// ============ FLEXIBLE PRICING MODEL THUNKS ============
export const switchPricingModel = createAsyncThunk(
  "Pricing/switchModel",
  async ({ newModel, reason = "Admin model switch" }) => {
    const response = await api.patch(`${PRICING_URL}/model`, { newModel, reason });
    return transformPricingResponse(response.data);
  },
);

export const updateBaseFeesModel = createAsyncThunk(
  "Pricing/updateBaseFees",
  async ({ metro, urban, suburban, rural, reason = "Admin base fees update" }) => {
    const response = await api.patch(`${PRICING_URL}/base-fees`, {
      metro,
      urban,
      suburban,
      rural,
      reason,
    });
    return transformPricingResponse(response.data);
  },
);

export const updateMultipliers = createAsyncThunk(
  "Pricing/updateMultipliers",
  async ({ high, standard, low, reason = "Admin multipliers update" }) => {
    const response = await api.patch(`${PRICING_URL}/multipliers`, {
      high,
      standard,
      low,
      reason,
    });
    return transformPricingResponse(response.data);
  },
);

export const updateDirectValues = createAsyncThunk(
  "Pricing/updateDirectValues",
  async ({ priceType, values, reason = "Admin direct values update" }) => {
    const response = await api.patch(`${PRICING_URL}/direct-values`, {
      priceType,
      values,
      reason,
    });
    return transformPricingResponse(response.data);
  },
);

export const saveOverride = createAsyncThunk(
  "Pricing/saveOverride",
  async (override) => {
    const payload = { ...override, reason: override.reason || "Admin override" };
    const response = await api.post(`${PRICING_URL}/overrides`, payload);
    return response.data;
  },
);

export const deleteOverride = createAsyncThunk(
  "Pricing/deleteOverride",
  async (overrideId) => {
    const response = await api.delete(`${PRICING_URL}/overrides/${overrideId}`);
    return response.data;
  },
);

export const fetchPricingModel = createAsyncThunk(
  "Pricing/fetchModel",
  async () => {
    const response = await api.get(PRICING_URL);
    return transformPricingResponse(response.data);
  },
);

// ============ SLICE ============
const slice = createSlice({
  name: "pricing",
  initialState: createPricingInitialState(),
  reducers: {
    clearMessage: (state) => {
      state.message = null;
    },
    addAuditLog: (state, action) => {
      const { action: auditAction, details, timestamp } = action.payload;
      addAuditEntry(state, action, auditAction, details);
    },
  },
  extraReducers: (builder) => {
    // Legacy Pricing Configs (Locality-based)
    createAsyncHandler(builder, getPricingConfigs, {
      onFulfilled: (state, action) => {
        state.configs = ensureArray(extractResponseData(action.payload));
      },
    });

    createAsyncHandler(builder, getPricingConfigByLocality, {
      onFulfilled: (state, action) => {
        const data = extractResponseData(action.payload);
        state.config = Array.isArray(data) ? data[0] : data ?? null;
      },
    });

    createAsyncHandler(builder, savePricingConfig, {
      onFulfilled: (state, action) => {
        state.message = action.payload.message;
        updateConfigByLocality(state, action.payload.data);
      },
    });

    createAsyncHandler(builder, updatePricingConfig, {
      onFulfilled: (state, action) => {
        state.message = action.payload.message;
        state.config = action.payload.data;
        updateConfigByLocality(state, action.payload.data);
      },
    });

    createAsyncHandler(builder, deletePricingConfig, {
      onFulfilled: (state, action) => {
        state.message = action.payload.message;
      },
    });

    // Flexible Pricing Models
    createAsyncHandler(builder, switchPricingModel, {
      loadingState: "modelSwitchInProgress",
      onFulfilled: (state, action) => {
        const data = action.payload;
        if (data?.pricingModel) {
          const oldModel = state.currentPricingModel;
          state.currentPricingModel = data.pricingModel;
          state.baseFeesModel = data.baseFeesModel || state.baseFeesModel;
          state.demandMultipliers = data.demandMultipliers || state.demandMultipliers;
          state.directValues = data.directValues || state.directValues;
          addAuditEntry(state, action, AUDIT_ACTIONS.MODEL_SWITCH, {
            from: oldModel,
            to: data.pricingModel,
          });
          state.message = "Model switched successfully";
        }
      },
    });

    createAsyncHandler(builder, updateBaseFeesModel, {
      onFulfilled: (state, action) => {
        const data = action.payload;
        if (data?.baseFeesModel) {
          state.baseFeesModel = data.baseFeesModel;
          addAuditEntry(state, action, AUDIT_ACTIONS.UPDATE_BASE_FEES, data.baseFeesModel);
          state.message = "Base fees updated successfully";
        }
      },
    });

    createAsyncHandler(builder, updateMultipliers, {
      onFulfilled: (state, action) => {
        const data = action.payload;
        if (data?.demandMultipliers) {
          state.demandMultipliers = data.demandMultipliers;
          addAuditEntry(state, action, AUDIT_ACTIONS.UPDATE_MULTIPLIERS, data.demandMultipliers);
          state.message = "Multipliers updated successfully";
        }
      },
    });

    createAsyncHandler(builder, updateDirectValues, {
      onFulfilled: (state, action) => {
        const data = action.payload;
        if (data?.directValues) {
          state.directValues = data.directValues;
          addAuditEntry(state, action, AUDIT_ACTIONS.UPDATE_DIRECT_VALUES, data.directValues);
          state.message = "Direct values updated successfully";
        }
      },
    });

    createAsyncHandler(builder, saveOverride, {
      onFulfilled: (state, action) => {
        const newOverride = extractResponseData(action.payload);
        if (newOverride?.id) {
          const index = state.overrides.findIndex((o) => o.id === newOverride.id);
          if (index >= 0) {
            state.overrides[index] = newOverride;
          } else {
            state.overrides.push(newOverride);
          }
          addAuditEntry(state, action, AUDIT_ACTIONS.SAVE_OVERRIDE, newOverride);
        }
        state.message = action.payload?.message || "Override saved";
      },
    });

    createAsyncHandler(builder, deleteOverride, {
      onFulfilled: (state, action) => {
        const overrideId = action.payload.data?.overrideId;
        state.overrides = state.overrides.filter((o) => o.id !== overrideId);
        addAuditEntry(state, action, AUDIT_ACTIONS.DELETE_OVERRIDE, { overrideId });
        state.message = action.payload.message;
      },
    });

    createAsyncHandler(builder, fetchPricingModel, {
      onFulfilled: (state, action) => {
        const data = action.payload;
        if (data?.pricingModel) {
          state.currentPricingModel = data.pricingModel;
          state.baseFeesModel = data.baseFeesModel || state.baseFeesModel;
          state.demandMultipliers = data.demandMultipliers || state.demandMultipliers;
          state.directValues = data.directValues || state.directValues;
          state.overrides = data.overrides || [];
          state.lastChanges = data.lastChanges || [];
        }
      },
    });
  },
});

export const { clearMessage, addAuditLog } = slice.actions;
export default slice.reducer;
